"use client";

import Link from "next/link";
import React, { useEffect, useState } from "react";
import { Article } from "../types";
import { fetchArticles, getArticleImage } from "../utils";
import { GuestNamesLinks } from "./GuestNamesLinks";
import clip from "text-clipper";
import { ArticleTags } from "./article-tags";
import { LoadMore } from "./load-more";

type Props = {
  filters: Record<string, unknown>;
  initialArticles?: Article[];
  initialPage: number;
  pageCount: number;
};

export const BlogPostRows = ({
  initialArticles = [],
  filters,
  initialPage,
  pageCount,
}: Props) => {
  const [page, setPage] = useState(initialPage);
  const [articles, setArticles] = useState<Article[]>(initialArticles);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (page === initialPage) return;

    setIsLoading(true);

      fetchArticles({ page, pageSize: 10, filters }).then((response) => {
        setArticles((prev) => [...prev, ...(response?.data ?? [])]);
      }).finally(() => setIsLoading(false))

  }, [page, initialPage, filters]);

  if (!articles || articles.length === 0) {
    return (
      <div className="w-full max-w-2xl py-16 mx-auto">
        <p className="p-4 text-center text-neutral-400">No results found</p>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="mx-auto divide-y grid grid-cols-1 lg:grid-cols-2 gap-5 pb-10">
        {articles.map((article, index) => (
          <BlogPostRow article={article} key={article.slug + index} />
        ))}
      </div>

      {pageCount > 1 && <LoadMore disabled={isLoading || page >= pageCount} isLoading={isLoading} onClick={() => setPage((p) => p + 1)} />}

    </div>
  );
};

export const BlogPostRow = ({ article }: { article: Article }) => {
  return (
    <div
      className="article border-neutral-200 flex flex-col justify-between
      p-[10px] md:p-[20px] xl:p-[24px]"
    >
      <div
        key={`${article.slug}`}
        className="flex items-start flex-row md:items-start group"
      >
        <div
          className="flex-shrink-0 w-full mb-4 md:mb-0 mr-2 md:mr-4 md:mt-2
          max-w-[120px] xs:max-w-[150px] md:max-w-[172px] xl:max-w-[210px]
          h-[200px] xs:h-[214px] md:h-[245px] xl:h-[300px] object-cover bg-center bg-no-repeat bg-cover rounded-lg"
          style={{
            backgroundImage: `url(${getArticleImage(article)})`,
            backgroundColor: "#fafafa",
          }}
        >
          <Link href={`/podcast/${article.slug}`}>
            {/* <Image
              src={getArticleImage(article)}
              alt={article.title}
              width={
                //@ts-expect-error fix types later
                article.bookImage?.formats?.small
                  ? //@ts-expect-error fix types later
                    article.bookImage?.formats?.small.width
                  : 210
              }
              height={
                //@ts-expect-error fix types later
                article.bookImage?.formats?.small
                  ? //@ts-expect-error fix types later
                    article.bookImage?.formats?.small.height
                  : 400
              }
              className="object-cover w-full rounded-lg mx-auto"
            /> */}
          </Link>
        </div>
        <div className="flex-1 w-full text-left">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between xs:leading-tight md:leading-tight xl:leading-tight">
            <h3
              className="lg:mt-1 mb-2 font-medium font-heading
              text-base xs:text-lg md:text-xl xl:text-2xl
              xl:leading-tight xs:leading-tight md:leading-tight"
            >
              <Link href={`/podcast/${article.slug}`}>{article.title}</Link>
            </h3>
          </div>
          <div
            className="font-body font-medium text-xs md:text-sm lg:text-base xl:text-lg"
          >
            <Link href={`/podcast/${article.slug}`}>
              <p className="mb-6 text-gray-700 leading-tight">
                {clip(article.excerpt, 240)}
              </p>
            </Link>
            <p className="article__footer">
              <span className="font-bold">Guest: </span>
              <span>
                <GuestNamesLinks guests={article.guest} />
              </span>
            </p>
            <p className="article__footer">
              <span className="font-bold">Date: </span>
              {new Date(article.publishedOn).toLocaleDateString()}
            </p>
            {article.publisher && (
              <span className="">
                <span className="font-bold">Publisher:</span>{" "}
                <Link
                  className="hover:underline"
                  href={`/publishers/${article.publisher.slug}`}
                >
                  {article.publisher.name}
                </Link>
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap">
        <ArticleTags article={article} />
      </div>
    </div>
  );
};
